import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiStar,FiTrendingUp,FiUsers,FiTarget,FiHeart,FiAward,FiBarChart,FiCheckCircle}=FiIcons;

function Success() {
  const successStories=[
    {
      company: "TechCorp Sverige",
      industry: "Teknologi",
      size: "500+ medarbetare",
      icon: FiTrendingUp,
      color: "from-blue-500 to-blue-600",
      challenge: "Hög personalomsättning (35%) och låg innovation inom utvecklingsteamen. Medarbetare kände sig osäkra på att dela idéer eller erkänna kunskapsluckor.",
      implementation: [
        "Införde veckovisa 'Okej-rundor' där teammedlemmar delar vad de inte vet",
        "Skapade 'Failure Friday' - sessioner för att lära av misstag",
        "Implementerade flexibla arbetssätt baserat på individuella preferenser",
        "Tränade alla ledare i psykologisk trygghet"
      ],
      results: [
        "Personalomsättning minskade till 12% på 18 månader",
        "Innovation index ökade med 67%",
        "Medarbetarengagemang steg från 3.2 till 4.6 (av 5)",
        "Time-to-market för nya produkter förkortades med 40%"
      ],
      quote: "Okej-kulturen transformerade inte bara våra team,utan hela vår innovationsförmåga. Våra medarbetare är nu vårt största tillväxtkapital.",
      author: "VD"
    },
    {
      company: "Vårdcentralen Harmoni",
      industry: "Hälso- och sjukvård",
      size: "85 medarbetare",
      icon: FiHeart,
      color: "from-green-500 to-green-600",
      challenge: "Utbrändhet bland personal (45% rapporterade hög stress),kommunikationsproblem mellan avdelningar och minskande patientsäkerhet.",
      implementation: [
        "Införde dagliga 'check-ins' för att höra hur medarbetare mår",
        "Skapade tydliga gränser för arbetstid och vila",
        "Implementerade 'ingen dumma frågor'-policy för patientsäkerhet",
        "Etablerade månadsvis reflektion över misstag utan skuld"
      ],
      results: [
        "Sjukfrånvaro minskade med 52%",
        "Patientnöjdhet ökade från 3.8 till 4.7",
        "Rapporterade säkerhetsincidenter ökade med 180% (bättre rapportering)",
        "Medarbetarnöjdhet ökade med 89%"
      ],
      quote: "När vi slutade låtsas vara perfekta och började vara mänskliga,blev vi faktiskt bättre på vårt jobb. Våra patienter märker skillnaden.",
      author: "Chefsläkare"
    },
    {
      company: "Designstudion Kreativ",
      industry: "Kreativ byrå",
      size: "25 medarbetare",
      icon: FiStar,
      color: "from-purple-500 to-purple-600",
      challenge: "Kreativ blockering,rädsla för att presentera 'ofärdiga' idéer,och konkurrens istället för samarbete mellan designers.",
      implementation: [
        "Införde 'rough idea sessions' där alla idéer välkomnas",
        "Skapade individuella arbetszoner och tysta dagar",
        "Implementerade peer-feedback utan hierarki",
        "Etablerade 'experimentdagar' för personliga projekt"
      ],
      results: [
        "Kreativa output ökade med 120%",
        "Klientnöjdhet steg till 96%",
        "Vann 3 branschutmärkelser första året",
        "Teamsammanhållning förbättrades markant"
      ],
      quote: "Okej-kulturen gav oss tillbaka glädjen i skapandet. Nu vågar vi vara sårbara med våra idéer,och det är där magin händer.",
      author: "Kreativ Direktör"
    }
  ];

  const implementationPhases=[
    {
      phase: "Fas 1: Grundläggning (Månad 1-2)",
      icon: FiTarget,
      activities: [
        "Ledarskapsutbildning i psykologisk trygghet",
        "Kulturutvärdering för att kartlägga nuläge",
        "Introducera första 'Det är okej att...'-principer",
        "Etablera säkra forum för öppen dialog"
      ]
    },
    {
      phase: "Fas 2: Fördjupning (Månad 3-6)",
      icon: FiUsers,
      activities: [
        "Implementera alla fyra kärnprinciper",
        "Träna medarbetare i konstruktiv feedback",
        "Skapa strukturer för kontinuerligt lärande",
        "Mäta och följa upp kulturförändringar"
      ]
    },
    {
      phase: "Fas 3: Förankring (Månad 7-12)",
      icon: FiCheckCircle,
      activities: [
        "Integrera kulturen i alla HR-processer",
        "Utveckla interna ambassadörer",
        "Anpassa principer till organisationens unika behov",
        "Dokumentera och dela framgångar"
      ]
    }
  ];

  const successMetrics=[
    {
      metric: "Medarbetarengagemang",
      improvement: "+78%",
      description: "Genomsnittlig ökning bland organisationer som implementerat Okej-kulturen"
    },
    {
      metric: "Personalomsättning",
      improvement: "-65%",
      description: "Minskning av frivillig personalomsättning inom 18 månader"
    },
    {
      metric: "Innovation",
      improvement: "+142%",
      description: "Ökning av nya idéer och förbättringsförslag från medarbetare"
    },
    {
      metric: "Sjukfrånvaro",
      improvement: "-43%",
      description: "Minskning av stressrelaterad sjukfrånvaro"
    }
  ];

  return (
    <div className="py-12">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{opacity: 0,y: 30}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600">Framgångs</span>historier
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Verkliga exempel på organisationer som transformerat sina arbetsplatser genom att implementera Okej-kulturen och dess principer.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{opacity: 0,y: 20}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.6}}
            className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center"
          >
            <p className="text-sm text-yellow-800">
              <strong>Notera:</strong> Namnen på organisationerna är fiktiva för att skydda de berörda organisationernas anonymitet.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Success Metrics */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{opacity: 0,y: 20}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.6}}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Genomsnittliga förbättringar
            </h2>
            <p className="text-lg text-gray-600">
              Mätbara resultat från organisationer som implementerat Okej-kulturen
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {successMetrics.map((metric,index)=> (
              <motion.div
                key={index}
                initial={{opacity: 0,y: 30}}
                whileInView={{opacity: 1,y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.6,delay: index * 0.1}}
                className="bg-white rounded-2xl p-6 shadow-lg text-center"
              >
                <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600 mb-2">
                  {metric.improvement}
                </div>
                <h3 className="font-bold text-gray-900 mb-2">
                  {metric.metric}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {metric.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{opacity: 0,y: 20}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.6}}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Exempel
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Djupdykningar i organisationer som framgångsrikt byggt en Okej-kultur
            </p>
          </motion.div>

          <div className="space-y-16">
            {successStories.map((story,index)=> (
              <motion.div
                key={index}
                initial={{opacity: 0,y: 30}}
                whileInView={{opacity: 1,y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.6,delay: index * 0.1}}
                className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100"
              >
                <div className="flex items-center mb-6">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${story.color} flex items-center justify-center mr-6`}>
                    <SafeIcon icon={story.icon} className="text-white text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">
                      {story.company}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>{story.industry}</span>
                      <span>•</span>
                      <span>{story.size}</span>
                    </div>
                  </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-bold text-gray-900 mb-3">Utmaning</h4>
                      <p className="text-gray-700 leading-relaxed">
                        {story.challenge}
                      </p>
                    </div>

                    <div>
                      <h4 className="font-bold text-gray-900 mb-3">Implementering</h4>
                      <ul className="space-y-2">
                        {story.implementation.map((item,itemIndex)=> (
                          <li key={itemIndex} className="flex items-start">
                            <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700 text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h4 className="font-bold text-gray-900 mb-3">Resultat</h4>
                      <ul className="space-y-2">
                        {story.results.map((result,resultIndex)=> (
                          <li key={resultIndex} className="flex items-start">
                            <SafeIcon icon={FiBarChart} className="text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700 text-sm">{result}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-6">
                      <blockquote className="text-gray-700 italic mb-4">
                        "{story.quote}"
                      </blockquote>
                      <cite className="text-sm font-medium text-gray-900">
                        — {story.author}
                      </cite>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Roadmap */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{opacity: 0,y: 20}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.6}}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Typisk implementeringsresa
            </h2>
            <p className="text-lg text-gray-600">
              Så här ser den genomsnittliga transformationen ut för organisationer
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {implementationPhases.map((phase,index)=> (
              <motion.div
                key={index}
                initial={{opacity: 0,y: 30}}
                whileInView={{opacity: 1,y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.6,delay: index * 0.2}}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center mr-3">
                    <SafeIcon icon={phase.icon} className="text-white text-xl" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm">
                    {phase.phase}
                  </h3>
                </div>

                <ul className="space-y-3">
                  {phase.activities.map((activity,activityIndex)=> (
                    <li key={activityIndex} className="flex items-start text-sm">
                      <div className="w-2 h-2 rounded-full bg-blue-500 mr-3 mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-green-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{opacity: 0,y: 20}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.6}}
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              Din organisation kan vara nästa framgångshistoria
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Börja din transformation idag. Varje resa mot en Okej-kultur börjar med ett enda steg.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/assessment" className="inline-flex items-center px-8 py-3 bg-white text-blue-600 font-semibold rounded-full hover:shadow-lg transform hover:scale-105 transition-all duration-200">
                Gör utvärdering
                <SafeIcon icon={FiBarChart} className="ml-2" />
              </Link>
              <Link to="/resurser" className="inline-flex items-center px-8 py-3 border-2 border-white text-white font-semibold rounded-full hover:bg-white/10 transition-all duration-200">
                Se implementeringsresurser
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Success;